import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PurchaseHist } from '../../services/types';
import { parseCalendarDate } from '../../utils/calendarDate';

interface PartPurchaseHistTableProps {
  purchaseHist: PurchaseHist[];
}

export const PartPurchaseHistTable: React.FC<PartPurchaseHistTableProps> = ({
  purchaseHist,
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      {/* Header */}
      <div className="bg-purple-50 px-4 py-3 border-b border-purple-200 flex-shrink-0">
        <h3 className="text-lg font-bold text-purple-900">
          {t('parts.purchaseHistory')}
        </h3>
        <p className="text-xs text-purple-700">
          {purchaseHist.length}{' '}
          {purchaseHist.length === 1
            ? t('parts.records')
            : t('parts.recordPlural')}
        </p>
      </div>

      {/* Content */}
      {purchaseHist.length > 0 ? (
        <div className="overflow-x-auto flex-grow">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.purchaseHistoryTable.poNum')}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.purchaseHistoryTable.vendor')}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.purchaseHistoryTable.orderDate')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.purchaseHistoryTable.qty')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.purchaseHistoryTable.unitCost')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.purchaseHistoryTable.leadTime')}
                </th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">
                  {t('parts.purchaseHistoryTable.status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {purchaseHist.map((record, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/purchase-orders/${record.poNum}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {record.poNum}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-xs truncate max-w-xs">
                    {record.vendorName}
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {formatDate(record.orderDate)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-medium">
                    {record.baseQty.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-medium">
                    ${record.unitCost.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-medium">
                    {record.leadTime} days
                  </td>
                  <td className="px-4 py-3 text-center">
                    {record.status ? (
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusStyle(
                          record.status
                        )}`}
                      >
                        {record.status}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="pb-2">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                PO #
              </p>
              <p className="text-sm font-medium text-gray-900">-</p>
            </div>
            <div className="pb-2 text-right">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                Qty
              </p>
              <p className="text-sm font-medium text-gray-900">0</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Format date string for display
 * Handles ISO format, timestamps, and various date formats
 */
function formatDate(dateString: string): string {
  if (!dateString) return '-';

  try {
    let date: Date;

    // Try parsing as ISO date first
    if (dateString.includes('T') || dateString.includes('-')) {
      date = parseCalendarDate(dateString);
    } else if (/^\d+$/.test(dateString)) {
      // Handle timestamp in milliseconds
      date = new Date(parseInt(dateString, 10));
    } else {
      // Try direct Date constructor
      date = new Date(dateString);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original if parsing failed
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * Get Tailwind classes for status badge styling
 * Open: green, Inspec: yellow, Closed: blue
 */
function getStatusStyle(status: string): string {
  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus === 'open') {
    return 'bg-green-100 text-green-800';
  } else if (normalizedStatus === 'inspec') {
    return 'bg-yellow-100 text-yellow-800';
  } else if (normalizedStatus === 'closed') {
    return 'bg-blue-100 text-blue-800';
  }

  // Default gray for unknown statuses
  return 'bg-gray-100 text-gray-800';
}
